import type { TranslationTable } from "@repo/shared/types";

const EN_GB = {
  signatureRequested: "Sent to the client for signing",
  signed: "Signed by the client",
  signatureRejected: "The client rejected signing",
  signatureExpired: "The signing request expired",
  submittedByClient: "Submitted by the client",
  completed: "Flow completed",
  complianceApproved: "Approved by compliance",
  complianceRejected: "Rejected by compliance",
  supportReply: "New reply on your support ticket",
  unnamedFlow: "Flow",
  unnamedTicket: "Support ticket",
  /** An event this build does not know yet (the API was deployed first). */
  unknownEvent: "New activity",
};

// All 17 locales: the bell and its toasts are shown in every UI language.
export const T: TranslationTable<typeof EN_GB> = {
  en_GB: EN_GB,
  fr_FR: {
    signatureRequested: "Envoyé au client pour signature",
    signed: "Signé par le client",
    signatureRejected: "Le client a refusé de signer",
    signatureExpired: "La demande de signature a expiré",
    submittedByClient: "Soumis par le client",
    completed: "Parcours terminé",
    complianceApproved: "Approuvé par la conformité",
    complianceRejected: "Rejeté par la conformité",
    supportReply: "Nouvelle réponse à votre ticket d'assistance",
    unnamedFlow: "Parcours",
    unnamedTicket: "Ticket d'assistance",
    unknownEvent: "Nouvelle activité",
  },
  es_ES: {
    signatureRequested: "Enviado al cliente para su firma",
    signed: "Firmado por el cliente",
    signatureRejected: "El cliente rechazó la firma",
    signatureExpired: "La solicitud de firma ha caducado",
    submittedByClient: "Enviado por el cliente",
    completed: "Flujo completado",
    complianceApproved: "Aprobado por cumplimiento",
    complianceRejected: "Rechazado por cumplimiento",
    supportReply: "Nueva respuesta en su ticket de soporte",
    unnamedFlow: "Flujo",
    unnamedTicket: "Ticket de soporte",
    unknownEvent: "Nueva actividad",
  },
  pt_BR: {
    signatureRequested: "Enviado ao cliente para assinatura",
    signed: "Assinado pelo cliente",
    signatureRejected: "O cliente recusou a assinatura",
    signatureExpired: "A solicitação de assinatura expirou",
    submittedByClient: "Enviado pelo cliente",
    completed: "Fluxo concluído",
    complianceApproved: "Aprovado pelo compliance",
    complianceRejected: "Rejeitado pelo compliance",
    supportReply: "Nova resposta no seu chamado de suporte",
    unnamedFlow: "Fluxo",
    unnamedTicket: "Chamado de suporte",
    unknownEvent: "Nova atividade",
  },
  de_DE: {
    signatureRequested: "Dem Kunden zur Unterschrift gesendet",
    signed: "Vom Kunden unterschrieben",
    signatureRejected: "Der Kunde hat die Unterschrift abgelehnt",
    signatureExpired: "Die Unterschriftsanfrage ist abgelaufen",
    submittedByClient: "Vom Kunden eingereicht",
    completed: "Ablauf abgeschlossen",
    complianceApproved: "Von Compliance genehmigt",
    complianceRejected: "Von Compliance abgelehnt",
    supportReply: "Neue Antwort auf Ihr Support-Ticket",
    unnamedFlow: "Ablauf",
    unnamedTicket: "Support-Ticket",
    unknownEvent: "Neue Aktivität",
  },
  it_IT: {
    signatureRequested: "Inviato al cliente per la firma",
    signed: "Firmato dal cliente",
    signatureRejected: "Il cliente ha rifiutato la firma",
    signatureExpired: "La richiesta di firma è scaduta",
    submittedByClient: "Inviato dal cliente",
    completed: "Flusso completato",
    complianceApproved: "Approvato dalla compliance",
    complianceRejected: "Respinto dalla compliance",
    supportReply: "Nuova risposta al tuo ticket di assistenza",
    unnamedFlow: "Flusso",
    unnamedTicket: "Ticket di assistenza",
    unknownEvent: "Nuova attività",
  },
  ar: {
    signatureRequested: "أُرسل إلى العميل للتوقيع",
    signed: "وقّعه العميل",
    signatureRejected: "رفض العميل التوقيع",
    signatureExpired: "انتهت صلاحية طلب التوقيع",
    submittedByClient: "أرسله العميل",
    completed: "اكتمل المسار",
    complianceApproved: "وافق عليه قسم الامتثال",
    complianceRejected: "رفضه قسم الامتثال",
    supportReply: "رد جديد على تذكرة الدعم الخاصة بك",
    unnamedFlow: "مسار",
    unnamedTicket: "تذكرة دعم",
    unknownEvent: "نشاط جديد",
  },
  ja_JP: {
    signatureRequested: "顧客に署名を依頼しました",
    signed: "顧客が署名しました",
    signatureRejected: "顧客が署名を拒否しました",
    signatureExpired: "署名依頼の期限が切れました",
    submittedByClient: "顧客が提出しました",
    completed: "フローが完了しました",
    complianceApproved: "コンプライアンスが承認しました",
    complianceRejected: "コンプライアンスが却下しました",
    supportReply: "サポートチケットに新しい返信があります",
    unnamedFlow: "フロー",
    unnamedTicket: "サポートチケット",
    unknownEvent: "新しいアクティビティ",
  },
  hi_IN: {
    signatureRequested: "हस्ताक्षर के लिए क्लाइंट को भेजा गया",
    signed: "क्लाइंट ने हस्ताक्षर किए",
    signatureRejected: "क्लाइंट ने हस्ताक्षर करने से इनकार किया",
    signatureExpired: "हस्ताक्षर अनुरोध की समय-सीमा समाप्त हो गई",
    submittedByClient: "क्लाइंट द्वारा सबमिट किया गया",
    completed: "फ़्लो पूरा हुआ",
    complianceApproved: "अनुपालन द्वारा स्वीकृत",
    complianceRejected: "अनुपालन द्वारा अस्वीकृत",
    supportReply: "आपके सहायता टिकट पर नया जवाब",
    unnamedFlow: "फ़्लो",
    unnamedTicket: "सहायता टिकट",
    unknownEvent: "नई गतिविधि",
  },
  zh_CN: {
    signatureRequested: "已发送给客户签署",
    signed: "客户已签署",
    signatureRejected: "客户拒绝签署",
    signatureExpired: "签署请求已过期",
    submittedByClient: "客户已提交",
    completed: "流程已完成",
    complianceApproved: "合规已批准",
    complianceRejected: "合规已拒绝",
    supportReply: "您的支持工单有新回复",
    unnamedFlow: "流程",
    unnamedTicket: "支持工单",
    unknownEvent: "新动态",
  },
  zh_TW: {
    signatureRequested: "已傳送給客戶簽署",
    signed: "客戶已簽署",
    signatureRejected: "客戶拒絕簽署",
    signatureExpired: "簽署請求已過期",
    submittedByClient: "客戶已提交",
    completed: "流程已完成",
    complianceApproved: "法遵已核准",
    complianceRejected: "法遵已駁回",
    supportReply: "您的支援工單有新回覆",
    unnamedFlow: "流程",
    unnamedTicket: "支援工單",
    unknownEvent: "新動態",
  },
  nl_NL: {
    signatureRequested: "Ter ondertekening naar de klant gestuurd",
    signed: "Ondertekend door de klant",
    signatureRejected: "De klant heeft ondertekenen geweigerd",
    signatureExpired: "Het ondertekeningsverzoek is verlopen",
    submittedByClient: "Ingediend door de klant",
    completed: "Flow voltooid",
    complianceApproved: "Goedgekeurd door compliance",
    complianceRejected: "Afgewezen door compliance",
    supportReply: "Nieuw antwoord op je supportticket",
    unnamedFlow: "Flow",
    unnamedTicket: "Supportticket",
    unknownEvent: "Nieuwe activiteit",
  },
  nb_NO: {
    signatureRequested: "Sendt til kunden for signering",
    signed: "Signert av kunden",
    signatureRejected: "Kunden avviste signeringen",
    signatureExpired: "Signeringsforespørselen utløp",
    submittedByClient: "Sendt inn av kunden",
    completed: "Flyten er fullført",
    complianceApproved: "Godkjent av compliance",
    complianceRejected: "Avvist av compliance",
    supportReply: "Nytt svar på supportsaken din",
    unnamedFlow: "Flyt",
    unnamedTicket: "Supportsak",
    unknownEvent: "Ny aktivitet",
  },
  sv_SE: {
    signatureRequested: "Skickat till kunden för signering",
    signed: "Signerat av kunden",
    signatureRejected: "Kunden avböjde att signera",
    signatureExpired: "Signeringsbegäran har gått ut",
    submittedByClient: "Inskickat av kunden",
    completed: "Flödet är slutfört",
    complianceApproved: "Godkänt av compliance",
    complianceRejected: "Avvisat av compliance",
    supportReply: "Nytt svar på ditt supportärende",
    unnamedFlow: "Flöde",
    unnamedTicket: "Supportärende",
    unknownEvent: "Ny aktivitet",
  },
  fi_FI: {
    signatureRequested: "Lähetetty asiakkaalle allekirjoitettavaksi",
    signed: "Asiakas allekirjoitti",
    signatureRejected: "Asiakas kieltäytyi allekirjoittamasta",
    signatureExpired: "Allekirjoituspyyntö vanheni",
    submittedByClient: "Asiakas lähetti",
    completed: "Prosessi valmis",
    complianceApproved: "Compliance hyväksyi",
    complianceRejected: "Compliance hylkäsi",
    supportReply: "Uusi vastaus tukipyyntöösi",
    unnamedFlow: "Prosessi",
    unnamedTicket: "Tukipyyntö",
    unknownEvent: "Uutta toimintaa",
  },
  da_DK: {
    signatureRequested: "Sendt til kunden til underskrift",
    signed: "Underskrevet af kunden",
    signatureRejected: "Kunden afviste at underskrive",
    signatureExpired: "Anmodningen om underskrift er udløbet",
    submittedByClient: "Indsendt af kunden",
    completed: "Forløbet er gennemført",
    complianceApproved: "Godkendt af compliance",
    complianceRejected: "Afvist af compliance",
    supportReply: "Nyt svar på din supportsag",
    unnamedFlow: "Forløb",
    unnamedTicket: "Supportsag",
    unknownEvent: "Ny aktivitet",
  },
  is_IS: {
    signatureRequested: "Sent til viðskiptavinar til undirritunar",
    signed: "Viðskiptavinur undirritaði",
    signatureRejected: "Viðskiptavinur hafnaði undirritun",
    signatureExpired: "Beiðni um undirritun rann út",
    submittedByClient: "Viðskiptavinur sendi inn",
    completed: "Ferli lokið",
    complianceApproved: "Samþykkt af regluvörslu",
    complianceRejected: "Hafnað af regluvörslu",
    supportReply: "Nýtt svar við þjónustubeiðni þinni",
    unnamedFlow: "Ferli",
    unnamedTicket: "Þjónustubeiðni",
    unknownEvent: "Ný virkni",
  },
};
